import {
  ColorSwatchPicker,
  FormInput,
  FormSelect,
  PrimaryButton,
  RedButton,
} from "../../../components";
import { useAlert } from "../../../store/useAlert";
import { useVouchers } from "../../../store/useVouchers";
import { usePartners } from "../../../store/usePartners";
import {
  usePartnerChallenges,
  IChallenge,
} from "../../../store/usePartnerChallenges";
import { useEffect, useMemo, useState } from "react";
import { Switch } from "@material-tailwind/react";

interface Props {
  open: boolean;
  onClose: () => void;
  partnerId?: string;
  partnerName?: string;
  onCreated?: () => void;
}

export const ChallengeVoucherModal = ({
  open,
  onClose,
  partnerId: initialPartnerId,
  partnerName,
  onCreated,
}: Props) => {
  const { addVoucher, addManyVoucher } = useVouchers();
  const { setAlert } = useAlert();
  const { allPartners, getPartnersForSelect } = usePartners();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const { allPartnerChallenges, getAllPartnerChallenges } =
    usePartnerChallenges();
  const [selectedChallengeId, setSelectedChallengeId] = useState<string>("");
  const [code, setCode] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [link, setLink] = useState("");
  const [benefitText, setBenefitText] = useState("");
  const [color, setColor] = useState("#FF5726");
  const [expireDate, setExpireDate] = useState("");
  const [allowMultipleRedeems, setAllowMultipleRedeems] =
    useState<boolean>(false);
  const [isProduct, setIsProduct] = useState<boolean>(false);
  const [prizeName, setPrizeName] = useState("");
  // Product vouchers only: how many physical prizes to create. Each one is a
  // separate single-use voucher, so the challenge can hand out exactly this
  // many and a repeating punchcard pays every completion until they run out.
  const [quantity, setQuantity] = useState("1");
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getPartnersForSelect();
    if (!allPartnerChallenges.length) {
      getAllPartnerChallenges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedPartnerId(initialPartnerId || "");
      setSelectedChallengeId("");
    } else {
      setCode("");
      setValue("");
      setDescription("");
      setInstructions("");
      setBenefitText("");
      setColor("#FF5726");
      setExpireDate("");
      setAllowMultipleRedeems(false);
      setQuantity("1");
      setSubmitted(false);
      setIsSaving(false);
      setSelectedPartnerId("");
      setSelectedChallengeId("");
    }
  }, [open, initialPartnerId]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleAddVoucher = async () => {
    setSubmitted(true);
    if (!selectedPartnerId) {
      return setAlert({
        isOpen: true,
        message: "Please select a partner before adding a voucher.",
        severity: "error",
      });
    }
    if (!isProduct && !code.trim()) {
      return;
    }
    const prizeCount = isProduct ? Number(quantity) : 1;
    if (!Number.isInteger(prizeCount) || prizeCount < 1 || prizeCount > 500) {
      return setAlert({
        isOpen: true,
        message: "Number of prizes must be a whole number from 1 to 500.",
        severity: "error",
      });
    }
    if (!selectedChallengeId) {
      return setAlert({
        isOpen: true,
        message: "Please select a challenge for challenge vouchers.",
        severity: "error",
      });
    }
    try {
      setIsSaving(true);
      const voucher = {
        code: isProduct ? undefined : code,
        value: isProduct ? undefined : (value || undefined),
        prizeName: isProduct ? (prizeName || undefined) : undefined,
        isProduct,
        description: description || undefined,
        instructions: instructions || undefined,
        link: link || undefined,
        benefitText: benefitText || undefined,
        color,
        expireDate: expireDate || undefined,
        partnerId: selectedPartnerId,
        challenge: selectedChallengeId || undefined,
        sourceType: "CHALLENGE",
        allowMultipleRedemptions: allowMultipleRedeems,
      } as const;
      if (prizeCount > 1) {
        const created = await addManyVoucher(
          Array.from({ length: prizeCount }, () => ({ ...voucher }))
        );
        if (!created || created.length !== prizeCount) {
          throw new Error("bulk create failed");
        }
      } else {
        await addVoucher(voucher);
      }
      onCreated?.();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        "We couldn't add the voucher right now. Please try again.";
      setAlert({
        isOpen: true,
        message,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const currentPartnerName = allPartners.find(
    (p) => p._id === selectedPartnerId
  )?.name;

  const getChallengePartnerId = (challenge: IChallenge): string => {
    const rawPartner = (challenge as any)?.partnerId;
    if (typeof rawPartner === "string") return rawPartner;
    if (rawPartner && typeof rawPartner === "object") {
      return String(rawPartner._id ?? rawPartner.id ?? "");
    }
    return "";
  };

  const partnerChallenges: IChallenge[] = useMemo(() => {
    if (!selectedPartnerId) return [];
    return allPartnerChallenges.filter(
      (c) => getChallengePartnerId(c) === selectedPartnerId
    );
  }, [allPartnerChallenges, selectedPartnerId]);

  if (!open) return null;

  return (
    <>
      <div className="justify-center items-center z-[10000000000] backdrop-blur-sm flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none">
        <div className="relative w-10/12 sm:w-1/3 my-0 sm:my-6 mx-auto max-w-3xl">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col max-h-[80vh] bg-white overflow-y-auto w-full outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-2xl font-semibold font-satoshi text-dark">
                Add challenge voucher
              </h3>
              <button
                className="text-gray-500"
                onClick={handleClose}
                disabled={isSaving}
              >
                ✕
              </button>
            </div>
            <div className="relative p-6 flex-auto space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partner *
                  </label>
                  <FormSelect
                    value={selectedPartnerId}
                    onChange={(e: any) => {
                      setSelectedPartnerId(e.target.value);
                      setSelectedChallengeId("");
                    }}
                    options={[
                      { value: "", label: "Select partner..." },
                      ...allPartners.map((partner) => ({
                        value: partner._id as string,
                        label: partner.name as string,
                      })),
                    ]}
                    placeholder="Select partner..."
                  />
                  {submitted && !selectedPartnerId && (
                    <p className="font-satoshi text-red-600 text-sm mt-1">
                      Partner is required*
                    </p>
                  )}
                </div>

                {selectedPartnerId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attach to challenge *
                    </label>
                    <FormSelect
                      value={selectedChallengeId}
                      onChange={(e: any) =>
                        setSelectedChallengeId(e.target.value)
                      }
                      options={[
                        ...partnerChallenges.map((challenge) => ({
                          value: challenge._id,
                          label: challenge.en_title || challenge.nl_title,
                        })),
                      ]}
                      placeholder="Select challenge..."
                    />
                    {submitted && !selectedChallengeId && (
                      <p className="font-satoshi text-red-600 text-sm mt-1">
                        Challenge is required*
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedPartnerId && (
                <p className="text-sm text-gray-600">
                  Voucher will be linked to{" "}
                  <strong>
                    {currentPartnerName ?? "the selected partner"}
                  </strong>
                  {selectedChallengeId
                    ? " and attached to the selected challenge."
                    : " and available for challenges for this partner."}
                </p>
              )}
              {!isProduct && (
                <>
                  <FormInput
                    label="Code *"
                    placeholder="Voucher code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  {submitted && !code ? (
                    <p className="font-satoshi text-red-600 text-sm">
                      Code is required*
                    </p>
                  ) : null}
                </>
              )}
              <div className="mt-2">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  label="Product voucher (physical prize)"
                  checked={isProduct}
                  onChange={(e) => {
                    const on = e?.target.checked;
                    setIsProduct(on);
                    if (on) setAllowMultipleRedeems(false);
                  }}
                />
                {isProduct && (
                  <p className="mt-1 text-xs text-amber-600">
                    Only supported in the redesign app. Current app users will not see this voucher correctly.
                  </p>
                )}
              </div>
              {isProduct ? (
                <>
                  <FormInput
                    label="Prize name"
                    placeholder="e.g., Thermos, Coffee mug"
                    value={prizeName}
                    onChange={(e) => setPrizeName(e.target.value)}
                  />
                  <FormInput
                    label="Number of prizes"
                    type="number"
                    min={1}
                    max={500}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Creates this many product vouchers. Each one goes to one
                    customer, so this is the number of prizes the store has.
                  </p>
                </>
              ) : (
                <>
                  <FormInput
                    label="Value"
                    placeholder="e.g., 2500 for €25"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A plain number is read as cents: 500 shows as &euro;5.00 and
                    5 as &euro;0.05. Write &euro;5, 20% or any text to have it
                    shown exactly as typed. Decimals are not supported.
                  </p>
                </>
              )}
              <FormInput
                type="date"
                label="Expire date"
                value={expireDate}
                onChange={(e) => setExpireDate(e.target.value)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Voucher description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Instructions for the user"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., https://www.partner.nl/actie"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Shown as one tappable line at the bottom of the voucher.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Benefit text
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="e.g., on all products"
                  value={benefitText}
                  onChange={(e) => setBenefitText(e.target.value)}
                />
              </div>
              <ColorSwatchPicker value={color} onChange={setColor} />
              <div className="mt-4">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  label="Allow multiple redeems"
                  checked={isProduct ? false : allowMultipleRedeems}
                  disabled={isProduct}
                  onChange={(e) => setAllowMultipleRedeems(e?.target.checked)}
                />
              </div>
            </div>
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              <RedButton
                label="Cancel"
                className="mr-4"
                onClick={handleClose}
              />
              <PrimaryButton
                label={isSaving ? "Saving..." : "Add voucher"}
                onClick={handleAddVoucher}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};
